/**
 * The search buttons in the header and the menu ask the boxes page to focus its search field: on
 * that page the shown field takes focus at once, and on the way there it takes the request when it
 * mounts. On a phone the field is hidden in the filters dialog, whose button reveals it first.
 */
let pending = false;
const listeners = new Set<() => void>();

function shown(element: Element): boolean {
  return element.getClientRects().length > 0;
}

/** Asks the boxes page's search field for focus. */
export function requestBoxSearchFocus(): void {
  pending = true;
  for (const listener of listeners) listener();
}

/** The ref callback for a search field; a hidden one leaves the request for one that is shown. */
export function boxSearchFocusRef(input: HTMLInputElement | null): (() => void) | undefined {
  if (!input) return undefined;
  const focus = () => {
    if (!pending || !shown(input)) return;
    pending = false;
    input.focus();
  };
  focus();
  listeners.add(focus);
  return () => {
    listeners.delete(focus);
  };
}

/**
 * A ref callback for the control that reveals a hidden search field (the phone's Filters button):
 * while it is shown, a request runs `reveal`, and the field it reveals takes the request on mount.
 */
export function boxSearchRevealRef(reveal: () => void) {
  return (control: HTMLElement | null): (() => void) | undefined => {
    if (!control) return undefined;
    const onRequest = () => {
      if (pending && shown(control)) reveal();
    };
    onRequest();
    listeners.add(onRequest);
    return () => {
      listeners.delete(onRequest);
    };
  };
}
